import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Chip, Avatar,
  CircularProgress, Alert, Stack,
} from '@mui/material';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import BaseDialog from './BaseDialog';
import GameSelectDialog, { GameSelectResult } from './GameSelectDialog';
import {
  HubCharacterDetail, hubSubmitContribution, hubUploadContributionImage,
} from '../../services/hubApi';

interface CharacterFormDialogProps {
  open: boolean;
  onClose: () => void;
  campaignId: number;
  /** 既存キャラ編集時に渡す。null/未指定なら新規作成。 */
  character?: HubCharacterDetail | null;
  onSubmitted: () => void;
}

const NAME_MAX = 255;
const DESC_MAX = 5000;

const CharacterFormDialog: React.FC<CharacterFormDialogProps> = ({
  open, onClose, campaignId, character, onSubmitted,
}) => {
  const isEdit = !!character;

  const [name, setName] = useState('');
  const [japaneseName, setJapaneseName] = useState('');
  const [description, setDescription] = useState('');
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [games, setGames] = useState<GameSelectResult[]>([]);
  const [reason, setReason] = useState('');

  const [gameDialogOpen, setGameDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setReason('');
    if (character) {
      setName(character.name || '');
      setJapaneseName(character.japanese_name || '');
      setDescription(character.description || '');
      setImageUrl(character.image_url || null);
      setGames(
        (character.games || []).map(g => ({
          game_id: g.id,
          name: g.name,
          japanese_name: g.name,
          cover: null,
        }))
      );
    } else {
      setName('');
      setJapaneseName('');
      setDescription('');
      setImageUrl(null);
      setGames([]);
    }
  }, [open, character]);

  const handlePickImage = () => fileInputRef.current?.click();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('画像は10MB以下にしてください');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const res = await hubUploadContributionImage(file);
      setImageUrl(res.image_url);
    } catch {
      setError('画像のアップロードに失敗しました');
    } finally {
      setUploading(false);
    }
  };

  const handleGamesSelected = (selected: GameSelectResult[]) => {
    setGames(prev => {
      const map = new Map(prev.map(g => [g.game_id, g]));
      selected.forEach(g => map.set(g.game_id, g));
      return Array.from(map.values());
    });
  };

  const removeGame = (gameId: number) =>
    setGames(prev => prev.filter(g => g.game_id !== gameId));

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError('名前は必須です');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await hubSubmitContribution({
        campaign_id: campaignId,
        target_id: character?.character_id ?? null,
        payload: {
          name: trimmedName,
          japanese_name: japaneseName.trim() || null,
          description: description.trim() || null,
          image_url: imageUrl,
          game_ids: games.map(g => g.game_id),
        },
        reason: reason.trim() || undefined,
      });
      onSubmitted();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || '送信に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const actions = (
    <>
      <Button onClick={onClose} size="small" disabled={submitting}>
        キャンセル
      </Button>
      <Button
        onClick={handleSubmit}
        variant="contained"
        size="small"
        disabled={submitting || uploading || !name.trim()}
        startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : undefined}
      >
        {isEdit ? '編集を申請' : '作成を申請'}
      </Button>
    </>
  );

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={isEdit ? 'キャラクターを編集' : 'キャラクターを追加'}
      maxWidth="sm"
      actions={actions}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 0.5 }}>
        <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ borderRadius: 2 }}>
          投稿は管理者の承認後に公開されます。承認されると貢献ポイントが付与されます。
        </Alert>

        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

        {/* 画像 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            src={imageUrl || undefined}
            variant="rounded"
            sx={{ width: 72, height: 72, bgcolor: 'rgba(255,255,255,0.06)' }}
          >
            {!imageUrl && <AddPhotoAlternateIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />}
          </Avatar>
          <Box>
            <Button
              size="small"
              variant="outlined"
              onClick={handlePickImage}
              disabled={uploading}
              startIcon={uploading ? <CircularProgress size={14} /> : <AddPhotoAlternateIcon />}
              sx={{ borderRadius: '100px', textTransform: 'none' }}
            >
              {imageUrl ? '画像を変更' : '画像を追加'}
            </Button>
            {imageUrl && (
              <Button size="small" color="inherit" onClick={() => setImageUrl(null)} sx={{ ml: 1 }}>
                削除
              </Button>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              JPEG / PNG / WebP・10MBまで
            </Typography>
          </Box>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            hidden
            onChange={handleFileChange}
          />
        </Box>

        <TextField
          label="名前"
          required
          fullWidth
          size="small"
          value={name}
          onChange={(e) => setName(e.target.value)}
          inputProps={{ maxLength: NAME_MAX }}
          placeholder="例: Cloud Strife"
        />
        <TextField
          label="日本語名"
          fullWidth
          size="small"
          value={japaneseName}
          onChange={(e) => setJapaneseName(e.target.value)}
          inputProps={{ maxLength: NAME_MAX }}
          placeholder="例: クラウド・ストライフ"
        />
        <TextField
          label="説明"
          fullWidth
          size="small"
          multiline
          minRows={3}
          maxRows={8}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          inputProps={{ maxLength: DESC_MAX }}
        />

        {/* 関連ゲーム */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" fontWeight={600}>関連ゲーム</Typography>
            <Button
              size="small"
              startIcon={<VideogameAssetIcon sx={{ fontSize: 16 }} />}
              onClick={() => setGameDialogOpen(true)}
              sx={{ borderRadius: '100px', textTransform: 'none' }}
            >
              ゲームを追加
            </Button>
          </Box>
          {games.length === 0 ? (
            <Typography variant="caption" color="text.secondary">
              このキャラクターが登場するゲームを追加してください
            </Typography>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={0.5}>
              {games.map(g => (
                <Chip
                  key={g.game_id}
                  label={g.japanese_name || g.name}
                  size="small"
                  onDelete={() => removeGame(g.game_id)}
                />
              ))}
            </Stack>
          )}
        </Box>

        {/* 編集理由 (編集時) */}
        {isEdit && (
          <TextField
            label="編集理由（任意）"
            fullWidth
            size="small"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="どこを・なぜ変更したか（審査の参考になります）"
          />
        )}
      </Box>

      <GameSelectDialog
        open={gameDialogOpen}
        onClose={() => setGameDialogOpen(false)}
        multiSelect
        showAllSearchOption
        title="関連ゲームを選択"
        excludeGameIds={games.map(g => g.game_id)}
        onMultiSelect={handleGamesSelected}
      />
    </BaseDialog>
  );
};

export default CharacterFormDialog;
